import { create } from "zustand";
import {
  handleFetchChats,
  handleLoadMoreChats,
  handleSearchChatsWithPagination,
  handleAddNewChat,
  handleUpdateChat,
  handleLoadMore,
  handleUpdateChatContact,
  handleDeleteChats,
  handleUpdateChatsReadStatus,
} from "./handlers";
import { Chat } from "@/entities/chat";

export interface NewContact {
  firstName: string;
  lastName: string;
  phone: string;
}

export interface ChatState {
  chats: Chat[];
  currentChat: Chat | null;
  loading: boolean;
  loadingChatIds: Record<string, boolean>;
  page: number;
  count: number;
  loadingMore: boolean;
  searchQuery: string;
  selectedChats: string[];

  fetchChats: (isAdmin?: boolean) => Promise<void>;
  loadMoreChats: () => Promise<void>;
  searchChatsWithPagination: (query: string, reset?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  getChat: (chatId: string) => Chat | undefined;

  updateReadStatus: (chatIds: string[], isUnread: boolean) => Promise<void>;
  selectChat: (chat: Chat) => void;
  clearChats: () => void;
  addNewChat: (newContact: NewContact) => Promise<Chat>;
  updateChat: (chatId: string, name: string) => Promise<void>;
  deleteChats: (chatIds: string[]) => Promise<void>;
  updateUnreadCount: (chatId: string, unreadCount: number) => void;
  updateChatContact: (chatId: string, firstName: string, lastName: string) => Promise<void>;
  toggleSelectChats: (chatId: string) => void;
  clearSelectedChats: () => void;
  setSelectedAllChats: () => void;
}

export const useChatStore = create<ChatState>((set, get) => {
  const context = {
    getState: get,
    setState: set,
  };

  return {
    chats: [],
    currentChat: null,
    selectedChats: [],
    loading: false,
    loadingChatIds: {},
    page: 1,
    count: 0,
    loadingMore: false,
    searchQuery: "",

    selectChat: (chat) => set({ currentChat: chat }),
    toggleSelectChats: (chatId) =>
      set((state) => {
        const newSelectedChats = state.selectedChats.includes(chatId)
          ? state.selectedChats.filter((chat) => chat !== chatId)
          : [...state.selectedChats, chatId];

        return { selectedChats: newSelectedChats };
      }),
    setSelectedAllChats: () =>
      set((state) => {
        const newSelectedChats = state.chats.map((chat) => chat._id);

        return { selectedChats: newSelectedChats };
      }),
    clearSelectedChats: () => set({ selectedChats: [] }),
    
    clearChats: () => set({ chats: [], currentChat: null, loadingChatIds: {} }),

    updateReadStatus: (chatIds: string[], isUnread: boolean) =>
      handleUpdateChatsReadStatus(context, chatIds, isUnread),

    updateUnreadCount: (chatId, unreadCount) => {
      set((state) => ({
        chats: state.chats.map((chat) =>
          chat._id === chatId ? { ...chat, unreadedMessageCount: unreadCount } : chat
        ),
      }));
    },

    fetchChats: (isAdmin) => handleFetchChats(context, isAdmin),
    loadMoreChats: () => handleLoadMoreChats(context),
    searchChatsWithPagination: (query, reset) =>
      handleSearchChatsWithPagination(context, query, reset),
    loadMore: () => handleLoadMore(context),
    getChat: (chatId) => get().chats.find((chat) => chat._id === chatId),
    addNewChat: (newContact) => handleAddNewChat(context, newContact),
    updateChat: (chatId, name) => handleUpdateChat(context, chatId, name),
    deleteChats: (chatIds) => handleDeleteChats(context, chatIds),
    updateChatContact: (chatId, firstName, lastName) =>
      handleUpdateChatContact(context, chatId, firstName, lastName), 
  };
});